import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Checkbox,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { IRootState } from "@/redux/types";
import { getFilteredCourses } from "@/redux/actions/courses";
import { getCoursetypes } from "@/redux/actions/coursetypes";

interface Props {
  open: boolean;
  onClose: () => void;
  selectedCourseIds: string[];
  onSave: (selectedIds: string[]) => void;
}

const CourseSelectorModal: FC<Props> = ({
  open,
  onClose,
  selectedCourseIds,
  onSave,
}) => {
  const { t } = useTranslation(["global"]);
  const dispatch = useDispatch();

  const { courses } = useSelector((state: IRootState) => state.course);
  const { coursetypes } = useSelector((state: IRootState) => state.coursetype);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [localSelected, setLocalSelected] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      dispatch(getCoursetypes());
      dispatch(getFilteredCourses({ coursetype_id: null, countries: [] }));
      setLocalSelected(selectedCourseIds);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      dispatch(
        getFilteredCourses({
          coursetype_id: selectedCategory ? Number(selectedCategory) : null,
          countries: [],
        })
      );
    }
  }, [selectedCategory]);

  const handleToggleCourse = (id: string) => {
    setLocalSelected((prev) => {
      const exists = prev.includes(id);
      return exists ? prev.filter((cid) => cid !== id) : [...prev, id];
    });
  };

  const filteredCourses = courses.filter((course: any) => {
    const matchesSearch = course.title
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t("select-courses")}</DialogTitle>
      <DialogContent>
        <Box display="flex" gap={2} mb={2}>
          <FormControl fullWidth>
            <InputLabel id="category-select-label">
              {t("category")}
            </InputLabel>
            <Select
              labelId="category-select-label"
              value={selectedCategory}
              label={t("category")}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <MenuItem value="">{t("all")}</MenuItem>
              {coursetypes.map((ct: any) => (
                <MenuItem key={ct.id} value={ct.id}>
                  {ct.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            placeholder={t("search-course")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Box>

        <Grid container spacing={2}>
          {filteredCourses.map((course: any) => (
            <Grid item xs={12} sm={6} md={4} key={course.id}>
              <Box border={1} borderRadius={1} p={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={localSelected.includes(course.id.toString())}
                      onChange={() =>
                        handleToggleCourse(course.id.toString())
                      }
                    />
                  }
                  label={<Typography>{course.title}</Typography>}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button variant="contained" onClick={() => onSave(localSelected)}>
          {t("save")} ({localSelected.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CourseSelectorModal;
